import React, { useState, useRef } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { useReactToPrint } from 'react-to-print';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Modal,
  Paper,
  TextField,
  Typography
} from '@material-ui/core';
import { DataGrid } from '@material-ui/data-grid';

import PurchaseOrderInvoice from 'components/Invoices/PurchaseOrderInvoice';

import { updatePurchaseOrderStatus, updatePurchaseOrderPayment, updatePurchaseOrderPaymentDate, setNewPOStatus, setNewPOPayments } from 'redux/actions';
import { Paid } from 'variables/status';
import { VAT_RECIPROCAL } from 'variables/globalVAT';

const mapStateToProps = (state) => ({
  newPurchaseOrder: state.newPurchaseOrder
});

const PurchaseOrderPaymentModal = (props) => {

  const printRef = useRef();

  const { displayId, vendorId, poProducts, status, total, payments } = props.newPurchaseOrder;
  
  const [switchy, setSwitchy] = useState(0);
  const [currAmount, setCurrAmount] = useState("");
  const [invoiceDisplayId, setInvoiceDisplayId] = useState("");

  const paidAmount = payments.reduce((prev, curr) => prev + curr.amount, 0);
  const pendingAmount = total - paidAmount;
  const currAmountWithoutVAT = Math.round( currAmount / VAT_RECIPROCAL );
  const amountVAT = currAmount - currAmountWithoutVAT;

  const columns = [
    { field: 'displayId', headerName: 'Invoice ID', flex: 1, sortable: false, disableColumnMenu: true },
    { field: 'date', headerName: 'Payment Date', flex: 1, sortable: false, disableColumnMenu: true },
    { field: 'amount', headerName: 'Amount', flex: 1, sortable: false, disableColumnMenu: true },
    { field: 'paymentMethod', headerName: 'Payment Method', flex: 1, sortable: false, disableColumnMenu: true }
  ];
  
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: "Purchase Order Invoice"
  });

  const createInvoiceDisplayId = () => {
    const invoiceNum = payments.length + 1;
    return (displayId + `/INV/${invoiceNum}`);
  };

  const handlePayment = (paymentByCash) => {
    const invoiceDisplayId = createInvoiceDisplayId();
    setInvoiceDisplayId(invoiceDisplayId);

    //This is a temporary method to get the purchaseOrder id. improve it. also PurchaseOrdersTable already
    //gets the purchaseOrderInfo using the find method, so here we are doing it twice.
    const purchaseOrderInfo = props.purchaseOrders.find(purchaseOrder => purchaseOrder.displayId === displayId && purchaseOrder.total === total);
    if(purchaseOrderInfo) {

      const updates = {
        vendorId,
        decrementAmount: currAmount
      };
      props.updatePurchaseOrderPayment({
        id: purchaseOrderInfo.id,
        payment: {
          displayId: invoiceDisplayId,
          amountVat: amountVAT,
          amount: currAmount,
          paymentByCash
        }
      }, updates);
      props.setNewPOPayments([
        ...payments,
        {
          displayId: invoiceDisplayId,
          amountVat: amountVAT,
          amount: currAmount,
          paymentByCash,
          createdAt: new Date()
        }
      ]);
      
      if( pendingAmount - currAmount <= 0 ) {
        props.updatePurchaseOrderStatus({ id: purchaseOrderInfo.id, status: Paid });
        props.updatePurchaseOrderPaymentDate({ id: purchaseOrderInfo.id });
        props.setNewPOStatus(Paid);
      } 
      setSwitchy(1);
    }
    else props.closeModal(false);
  };

  const createInvoiceInfo = () => {
    const vendorInfo = props.vendors.find(vendor => vendor.id === vendorId);
    return {
      displayId: invoiceDisplayId || (`${displayId}/DRAFT`),
      vendorName: vendorInfo.name,
      vendorTrn: vendorInfo.trn,
      paymentDate: new Date(),
      subTotal: currAmountWithoutVAT,
      taxTotal: amountVAT,
      currentAmount: currAmount,
      grandTotal: total,
      products: poProducts,
      payments,
      status
    }
  };

  const renderAll = () => {
    switch (switchy) {
      case 0:
        return (
          <Box>
            <Box
              sx={{
                overflow: 'auto',
                height: 266,
                marginBottom: '1%'
              }}
            >
              <DataGrid
                //'id' field is added only for the DataGrid object.
                rows={payments.map(payment => ({
                  id: payment.displayId,
                  displayId: payment.displayId,
                  date: moment(payment.createdAt).format('DD/MM/YYYY'),
                  amount: payment.amount,
                  paymentMethod: payment.paymentByCash ? "Cash" : "Card"
                }))}
                columns={columns}
                disableSelectionOnClick
                hideFooter
                localeText={{
                  noRowsLabel: 'No Payments'
                }}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              <Typography
                color="textPrimary"
                variant="h6"
                gutterBottom
              >
                {`Pending Amount : AED ${Number(pendingAmount).toFixed(2)}`}
              </Typography>
              <TextField
                helperText="Please Enter the Current Invoice Amount"
                label="Invoice Amount"
                name="currAmount"
                onChange={event => setCurrAmount( parseFloat(event.target.value) || 0 )}
                value={currAmount}
                variant="outlined"
              />
              <Typography
                color="textPrimary"
                variant="h6"
                gutterBottom
              >
                {//This Displays the Current Invoice Amount with the VAT included.
                `Invoice Amount Breakdown : AED ${currAmountWithoutVAT} + ${amountVAT} ( VAT )`}
              </Typography>
              <Typography
                color="textPrimary"
                variant="h6"
                gutterBottom
              >
                {//This Displays the Balance Amount that will be left after current payment with VAT included.
                `Balance Amount : AED ${Math.round( pendingAmount - currAmount < 0 ? 0 : pendingAmount - currAmount )}`}
              </Typography>
              { currAmount <= 0 ?
                <Button
                  variant="contained"
                  color="primary"
                  onClick={setSwitchy.bind(this, 1)}
                >
                  Print Invoice
                </Button> :
                <Box sx={{ display: 'flex' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handlePayment.bind(this, true)}
                  >
                    Cash Payment
                  </Button>
                  <Divider
                    style={{ margin: '10px' }}
                    orientation="vertical"
                    flexItem
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handlePayment.bind(this, false)}
                  >
                    Card Payment
                  </Button>
                </Box>
              }
            </Box>
          </Box>
        )
      
      case 1:
        return (
          <Box>
            <Paper
              style={{
                overflow: 'auto',
                maxHeight: 462
              }}
            >
              <PurchaseOrderInvoice
                ref={printRef}
                info={createInvoiceInfo()}
                user={props.user}
              />
            </Paper>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                paddingTop: '1%'
              }}
            >
              <Button
                color="primary"
                variant="contained"
                onClick={() => {
                  handlePrint();
                  props.closeModal(false);
                }}
              >
                Print Invoice
              </Button>
            </Box>
          </Box>
        )
      
      default:
        return (<React.Fragment></React.Fragment>)
    }
  };

  return (
    <Modal open={props.open} >
      <Card>
        <CardHeader
          subheader="Please Review Previous Payments."
          title="Make Payment"
        />
        <Divider />
        <CardContent>
          {renderAll()}
        </CardContent>
      </Card>
    </Modal>
  )
};

export default connect (mapStateToProps, {updatePurchaseOrderStatus, updatePurchaseOrderPayment, updatePurchaseOrderPaymentDate, setNewPOStatus, setNewPOPayments}) (PurchaseOrderPaymentModal);